import { Options, SeriesOptionsType } from 'highcharts';
import moment from 'moment';
import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
// eslint-disable-next-line import/no-internal-modules
import {
  FinancialsTooltipKeys,
  ProjectFinancialsContractType,
  ProjectFinancialsZoomLevel,
} from 'api/project';
import { currencyRenderer } from 'components/atoms/currency-renderer';
import { ProjectFinancialsContext } from 'contexts/project-financials';
import { queueMacrotask } from 'utils/macrotask';
import { projectFinancialsBarChartOptions } from '../utils/charts/bar';
import {
  ProjectFinancialsBarChartColorMap,
  projectFinancialsBaseYAxisOptions,
} from '../utils/charts/common';
import {
  getBarChartDataForZoomLevel,
  minsToHours,
  percentageRenderer,
} from '../utils/common';
import { useProjectFinancialsLabels } from './labels';
import { useFinancialsTooltips } from './tooltips';

interface ReturnProps {
  budgetBarChartOptions: Options | undefined;
  budgetBarChartTooltipContent: ReactNode;
  hoursBarChartOptions: Options | undefined;
  budgetBarChartZoomLevel: ProjectFinancialsZoomLevel;
  hoursBarChartZoomLevel: ProjectFinancialsZoomLevel;
  budgetBarChartDataLoading: boolean;
  hoursBarChartDataLoading: boolean;
  onBudgetBarChartZoomLevelChange: (value: ProjectFinancialsZoomLevel) => void;
  onHoursBarChartZoomLevelChange: (value: ProjectFinancialsZoomLevel) => void;
}

export const useProjectFinancialsBarCharts = (): ReturnProps => {
  const context = useContext(ProjectFinancialsContext);
  const {
    currencyCode,
    projectFinancials: {
      barChartData,
      projectFinancialDetails: { financialContractType, projectBudget },
    },
    projectFinancialsVisibilityConfig: { profitabilityData, allocationData },
    permissionsMap: {
      projectProfitability: { canRead: canViewProfitability },
    },
    revenueLabels,
    timeLabels,
    percentageLabels,
  } = context;

  const {
    costLabel,
    marginLabel,
    revenueLabel,
    estimatedCostLabel,
    estimatedRevenueLabel,
    timeAllocatedLabel,
    billableTimeTrackedLabel,
    nonBillableTimeTrackedLabel,
  } = useProjectFinancialsLabels({ financialContractType });

  const { getTooltipContent } = useFinancialsTooltips({
    financialContractType,
  });

  const getChartOptions = useMemo(
    () =>
      projectFinancialsBarChartOptions({
        currencyCode,
        revenueLabels,
        timeLabels,
        percentageLabels,
      }),
    [currencyCode, percentageLabels, revenueLabels, timeLabels],
  );

  const [budgetBarChartZoomLevel, setBudgetBarChartZoomLevel] =
    useState<ProjectFinancialsZoomLevel>(ProjectFinancialsZoomLevel.Monthly);
  const [hoursBarChartZoomLevel, setHoursBarChartZoomLevel] =
    useState<ProjectFinancialsZoomLevel>(ProjectFinancialsZoomLevel.Monthly);

  const [budgetBarChartData, setBudgetBarChartData] = useState(barChartData);
  const [hoursBarChartData, setHoursBarChartData] = useState(barChartData);
  const [budgetBarChartDataLoading, setBudgetBarChartDataLoading] =
    useState(false);
  const [hoursBarChartDataLoading, setHoursBarChartDataLoading] =
    useState(false);

  const onBudgetBarChartZoomLevelChange = useCallback(
    (value: ProjectFinancialsZoomLevel) => {
      if (barChartData == null) {
        return;
      }

      setBudgetBarChartDataLoading(true);
      setBudgetBarChartZoomLevel(value);
      setBudgetBarChartData(
        Object.values(
          getBarChartDataForZoomLevel(
            barChartData,
            value,
            financialContractType,
            projectBudget ?? 0,
          ),
        ),
      );
      queueMacrotask(() => {
        setBudgetBarChartDataLoading(false);
      });
    },
    [barChartData, financialContractType, projectBudget],
  );

  const onHoursBarChartZoomLevelChange = useCallback(
    (value: ProjectFinancialsZoomLevel) => {
      if (barChartData == null) {
        return;
      }

      setHoursBarChartDataLoading(true);
      setHoursBarChartZoomLevel(value);
      setHoursBarChartData(
        Object.values(
          getBarChartDataForZoomLevel(
            barChartData,
            value,
            financialContractType,
            projectBudget ?? 0,
          ),
        ),
      );
      queueMacrotask(() => {
        setHoursBarChartDataLoading(false);
      });
    },
    [barChartData, financialContractType, projectBudget],
  );

  const budgetBarChartOptions: Options | undefined = useMemo(() => {
    if (
      budgetBarChartData == null ||
      (financialContractType === ProjectFinancialsContractType.NONBILLABLE &&
        !profitabilityData)
    ) {
      return undefined;
    }

    const estimatedRevenueData: number[][] = [];
    const actualRevenueData: number[][] = [];
    const estimatedCostData: number[][] = [];
    const actualCostData: number[][] = [];
    const marginData: number[][] = [];
    const xAxisDates: number[] = [];

    budgetBarChartData.forEach(
      ({
        startDate,
        estimatedRevenue,
        actualRevenue,
        estimatedCost,
        actualCost,
        margin,
      }) => {
        const startDateMs = moment(startDate).startOf('day').utc().valueOf();

        if (estimatedRevenue != null) {
          estimatedRevenueData.push([startDateMs, estimatedRevenue]);
        }
        if (actualRevenue != null) {
          actualRevenueData.push([startDateMs, actualRevenue]);
        }
        if (estimatedCost != null) {
          estimatedCostData.push([startDateMs, estimatedCost]);
        }
        if (actualCost != null) {
          actualCostData.push([startDateMs, actualCost]);
        }
        if (margin != null) {
          marginData.push([startDateMs, margin]);
        }
        xAxisDates.push(startDateMs);
      },
    );

    const estimatedRevenueColumn: SeriesOptionsType = {
      type: 'column',
      name: estimatedRevenueLabel,
      stack: 'Allocated',
      centerInCategory: true,
      yAxis: 0,
      legendIndex: 0,
      color:
        ProjectFinancialsBarChartColorMap[financialContractType]
          .estimatedRevenue,
      data: estimatedRevenueData,
    };
    const actualRevenueColumn: SeriesOptionsType = {
      type: 'column',
      name: revenueLabel,
      stack: 'Tracked',
      centerInCategory: true,
      yAxis: 0,
      legendIndex: 1,
      color:
        ProjectFinancialsBarChartColorMap[financialContractType].actualRevenue,
      data: actualRevenueData,
    };
    const estimatedCostColumn: SeriesOptionsType = {
      type: 'column',
      name: estimatedCostLabel,
      stack: 'Allocated',
      centerInCategory: true,
      yAxis: 0,
      legendIndex: 0,
      color:
        ProjectFinancialsBarChartColorMap[financialContractType].estimatedCost,
      data: estimatedCostData,
    };
    const actualCostColumn: SeriesOptionsType = {
      type: 'column',
      name: costLabel,
      stack: 'Tracked',
      centerInCategory: true,
      yAxis: 0,
      legendIndex: 1,
      color:
        ProjectFinancialsBarChartColorMap[financialContractType].actualCost,
      data: actualCostData,
    };
    const marginColumn: SeriesOptionsType = {
      type: 'spline',
      name: marginLabel,
      yAxis: 1,
      zIndex: 15,
      legendIndex: 2,
      color: ProjectFinancialsBarChartColorMap[financialContractType].margin,
      data: marginData,
    };

    const options: Options = {
      ...getChartOptions({
        chartZoomLevel: budgetBarChartZoomLevel,
        extraPlotOptions: {
          spline: {
            marker: {
              symbol: 'circle',
              radius: 4,
            },
          },
        },
        xAxisDates,
      }),

      yAxis: [
        projectFinancialsBaseYAxisOptions({
          labels: {
            formatter: (obj) =>
              currencyRenderer(parseInt(obj.value, 10), currencyCode),
          },
        }),
        projectFinancialsBaseYAxisOptions({
          opposite: true,
          labels: {
            formatter: (obj) => percentageRenderer(obj.value),
          },
        }),
      ],

      series: [
        ...(financialContractType === ProjectFinancialsContractType.NONBILLABLE
          ? [
              ...(canViewProfitability && profitabilityData
                ? [
                    ...(allocationData ? [estimatedCostColumn] : []),
                    actualCostColumn,
                  ]
                : []),
            ]
          : [
              ...(allocationData ? [estimatedRevenueColumn] : []),
              actualRevenueColumn,
              ...(canViewProfitability && profitabilityData
                ? [marginColumn]
                : []),
            ]),
      ],
    };

    return options;
  }, [
    allocationData,
    budgetBarChartData,
    budgetBarChartZoomLevel,
    canViewProfitability,
    costLabel,
    currencyCode,
    estimatedCostLabel,
    estimatedRevenueLabel,
    financialContractType,
    getChartOptions,
    marginLabel,
    profitabilityData,
    revenueLabel,
  ]);

  const budgetBarChartTooltipContent = useMemo(
    () =>
      budgetBarChartDataLoading ||
      budgetBarChartData == null ||
      (financialContractType === ProjectFinancialsContractType.NONBILLABLE &&
        !profitabilityData)
        ? undefined
        : getTooltipContent(
            [
              ...(financialContractType ===
              ProjectFinancialsContractType.NONBILLABLE
                ? ['cost']
                : [
                    'revenue',
                    ...(canViewProfitability && profitabilityData
                      ? ['margin']
                      : []),
                  ]),
            ] as FinancialsTooltipKeys[],
            allocationData,
          ),
    [
      allocationData,
      budgetBarChartData,
      budgetBarChartDataLoading,
      canViewProfitability,
      financialContractType,
      getTooltipContent,
      profitabilityData,
    ],
  );

  const hoursBarChartOptions: Options | undefined = useMemo(() => {
    if (hoursBarChartData == null) {
      return undefined;
    }

    const timeAllocatedData: number[][] = [];
    const billableTimeTrackedData: number[][] = [];
    const nonBillableTimeTrackedData: number[][] = [];
    const xAxisDates: number[] = [];

    hoursBarChartData.forEach(
      ({
        startDate,
        timeAllocated,
        billableTimeTracked,
        nonBillableTimeTracked,
      }) => {
        const startDateMs = moment(startDate).startOf('day').utc().valueOf();

        if (timeAllocated != null) {
          timeAllocatedData.push([startDateMs, minsToHours(timeAllocated)]);
        }
        if (billableTimeTracked != null) {
          billableTimeTrackedData.push([
            startDateMs,
            minsToHours(billableTimeTracked),
          ]);
        }
        if (nonBillableTimeTracked != null) {
          nonBillableTimeTrackedData.push([
            startDateMs,
            minsToHours(nonBillableTimeTracked),
          ]);
        }
        xAxisDates.push(startDateMs);
      },
    );

    const timeAllocatedColumn: SeriesOptionsType = {
      type: 'column',
      name: timeAllocatedLabel,
      stack: 'Allocated',
      centerInCategory: true,
      legendIndex: 0,
      color:
        ProjectFinancialsBarChartColorMap[financialContractType].timeAllocated,
      data: timeAllocatedData,
    };
    const billabledTimeTrackedColumn: SeriesOptionsType = {
      type: 'column',
      name: billableTimeTrackedLabel,
      stack: 'Tracked',
      centerInCategory: true,
      legendIndex: 1,
      color:
        ProjectFinancialsBarChartColorMap[financialContractType]
          .billableTimeTracked,
      data: billableTimeTrackedData,
    };
    const nonBillableTimeTrackedColumn: SeriesOptionsType = {
      type: 'column',
      name: nonBillableTimeTrackedLabel,
      stack: 'Tracked',
      centerInCategory: true,
      legendIndex: 2,
      color:
        ProjectFinancialsBarChartColorMap[financialContractType]
          .nonBillableTimeTracked,
      data: nonBillableTimeTrackedData,
    };

    const options: Options = {
      ...getChartOptions({
        chartZoomLevel: hoursBarChartZoomLevel,
        xAxisDates,
      }),

      yAxis: [
        projectFinancialsBaseYAxisOptions({
          min: 0,
          labels: {
            format: '{value}h',
          },
        }),
      ],

      series: [
        ...(allocationData ? [timeAllocatedColumn] : []),
        nonBillableTimeTrackedColumn,
        ...(financialContractType !== ProjectFinancialsContractType.NONBILLABLE
          ? [billabledTimeTrackedColumn]
          : []),
      ],
    };

    return options;
  }, [
    allocationData,
    billableTimeTrackedLabel,
    financialContractType,
    getChartOptions,
    hoursBarChartData,
    hoursBarChartZoomLevel,
    nonBillableTimeTrackedLabel,
    timeAllocatedLabel,
  ]);

  return {
    budgetBarChartOptions,
    budgetBarChartTooltipContent,
    hoursBarChartOptions,
    budgetBarChartZoomLevel,
    hoursBarChartZoomLevel,
    budgetBarChartDataLoading,
    hoursBarChartDataLoading,
    onBudgetBarChartZoomLevelChange,
    onHoursBarChartZoomLevelChange,
  };
};
